import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useLoading } from '../../hooks/useLoading';
import { useNotifications } from '../../hooks/useNotifications';
import {
  getEmployeesRequest,
  updateDocumentRepositoryFile
} from '../../constants/requests';

function dedupeAccessControl(rows) {
  const map = new Map();
  for (const row of rows) {
    const id = (row.employeeId ?? '').trim();
    if (!id) continue;
    map.set(id, { employeeId: id, accessLevel: row.accessLevel || 'view' });
  }
  return Array.from(map.values());
}

export default function FileAccessDialog({
  open,
  file,
  employeeId,
  onClose,
  onSaved
}) {
  const { request } = useLoading();
  const { showNotifications } = useNotifications();
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);

  const fileId = file?._id ?? file?.id;

  useEffect(() => {
    if (!open) return;
    const initial = Array.isArray(file?.accessControl)
      ? file.accessControl
      : [];
    setRows(
      initial.map((r) => ({
        employeeId: r.employeeId ?? '',
        accessLevel: r.accessLevel ?? 'view'
      }))
    );
  }, [open, file]);

  useEffect(() => {
    if (!open) return;
    if (employeesLoading || employees.length > 0) return;

    const fetchEmployees = async () => {
      setEmployeesLoading(true);
      try {
        const response = await request(() => getEmployeesRequest());
        const list = response?.data?.body ?? response?.data?.data;
        setEmployees(Array.isArray(list) ? list : []);
      } catch (err) {
        setEmployees([]);
        showNotifications('error', 'Failed to load employees', 4000);
      } finally {
        setEmployeesLoading(false);
      }
    };

    fetchEmployees();
    // eslint-disable-next-line
  }, [open]);

  const employeeOptions = useMemo(() => {
    return (employees || [])
      .map((emp) => {
        const id = emp?._id ?? emp?.id;
        const fname = emp?.fname ?? '';
        const lname = emp?.lname ?? '';
        const label = `${fname} ${lname}`.trim();
        return { id, label };
      })
      .filter((x) => x.id);
  }, [employees]);

  const addRow = () => {
    setRows((prev) => [...prev, { employeeId: '', accessLevel: 'view' }]);
  };

  const removeRow = (idx) => {
    setRows((prev) => prev.filter((_, i) => i !== idx));
  };

  const updateRow = (idx, field, value) => {
    setRows((prev) =>
      prev.map((r, i) => (i === idx ? { ...r, [field]: value } : r))
    );
  };

  const handleSave = async () => {
    if (!fileId) {
      showNotifications('error', 'Missing file id', 4000);
      return;
    }
    if (!employeeId) {
      showNotifications('error', 'Missing employee id', 4000);
      return;
    }

    const accessControl = dedupeAccessControl(rows);
    setSaving(true);
    try {
      const response = await request(() =>
        updateDocumentRepositoryFile(
          fileId,
          { employeeId, accessControl },
          employeeId
        )
      );

      if (response?.data?.success) {
        showNotifications('success', 'Access updated', 3000);
        onClose();
        onSaved?.();
      } else {
        showNotifications(
          'error',
          response?.data?.message ?? 'Failed to update access',
          5000
        );
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Manage access</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Add or change who can view/edit this file.
        </Typography>
        {rows.map((row, idx) => (
          <Box
            key={idx}
            sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}
          >
            <FormControl size="small" sx={{ flex: 1 }}>
              <InputLabel>Employee</InputLabel>
              <Select
                value={row.employeeId || ''}
                label="Employee"
                onChange={(e) => updateRow(idx, 'employeeId', e.target.value)}
                disabled={saving || employeesLoading}
                displayEmpty
              >
                <MenuItem value="">
                  <em>{employeesLoading ? 'Loading…' : 'Select employee'}</em>
                </MenuItem>
                {employeeOptions.map((opt) => (
                  <MenuItem key={opt.id} value={opt.id}>
                    {opt.label ? `${opt.label} (${opt.id})` : opt.id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 110 }}>
              <InputLabel>Access</InputLabel>
              <Select
                value={row.accessLevel || 'view'}
                label="Access"
                onChange={(e) => updateRow(idx, 'accessLevel', e.target.value)}
                disabled={saving}
              >
                <MenuItem value="view">View</MenuItem>
                <MenuItem value="edit">Edit</MenuItem>
              </Select>
            </FormControl>
            <IconButton
              size="small"
              onClick={() => removeRow(idx)}
              disabled={saving}
              aria-label="Remove"
            >
              <DeleteOutlineIcon />
            </IconButton>
          </Box>
        ))}
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={addRow}
          disabled={saving}
        >
          Add person
        </Button>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
